import { createContext, type Dispatch, useContext } from "react";

import type { EventFilterScope } from "@/features/events/types/event-filter-scope";

import { type EventsActionPayload, type EventsState } from "@/context/events";

export interface EventFiltersRegistry {
	allActivity: ScopedEventsSlice;
	dashboard: ScopedEventsSlice;
}

export interface ScopedEventsSlice {
	dispatch: Dispatch<EventsActionPayload>;
	state: EventsState;
}

export const EventFiltersRegistryContext = createContext<
	EventFiltersRegistry | undefined
>(undefined);

export const useEventFiltersRegistry = (): EventFiltersRegistry => {
	const context = useContext(EventFiltersRegistryContext);
	if (context === undefined) {
		throw new Error(
			"useEventFiltersRegistry must be used within an EventFiltersProvider",
		);
	}
	return context;
};

const registryKey = (scope: EventFilterScope): keyof EventFiltersRegistry =>
	scope === "dashboard" ? "dashboard" : "allActivity";

export const useScopedEventsState = (scope: EventFilterScope): EventsState =>
	useEventFiltersRegistry()[registryKey(scope)].state;

export const useScopedEventsDispatch = (
	scope: EventFilterScope,
): Dispatch<EventsActionPayload> =>
	useEventFiltersRegistry()[registryKey(scope)].dispatch;

export const useDashboardEventsState = (): EventsState =>
	useEventFiltersRegistry().dashboard.state;

export const useDashboardEventsDispatch = (): Dispatch<EventsActionPayload> =>
	useEventFiltersRegistry().dashboard.dispatch;

export const useAllActivityEventsState = (): EventsState =>
	useEventFiltersRegistry().allActivity.state;

export const useAllActivityEventsDispatch = (): Dispatch<EventsActionPayload> =>
	useEventFiltersRegistry().allActivity.dispatch;
